import { InputSummaryItem } from '../../../../input-summary';
import InputSummaryUtils from '../../../../../helper/input-summary/input-summary-util';
import { UnitValue } from '../../../../data-structures/unit-value.model';

/**
 * An interface used to define the class structure for the feedstock bmp potential AI1 model
 */
export interface FeedstockBmpPotentialAI1Interface {
  lipidFat: UnitValue;
  protein: UnitValue;
  hemicellulose: UnitValue;
  cellulose: UnitValue;
  lignin: UnitValue;
  nonFibreCarbohydrate: UnitValue;
  ch4ContentEnabled: boolean;
  ch4Content: UnitValue;
}

/**
 * A class that contains members for the feedstock BMP potential AI1 model
 */
export class FeedstockBmpPotentialAI1 implements FeedstockBmpPotentialAI1Interface {
  lipidFat: UnitValue;
  protein: UnitValue;
  hemicellulose: UnitValue;
  cellulose: UnitValue;
  lignin: UnitValue;
  nonFibreCarbohydrate: UnitValue;
  ch4ContentEnabled: boolean;
  ch4Content: UnitValue;

  /**
   * constructs the FeedstockBmpPotentialElemental class.
   * @param param is used to construct the class. If null or not provided, the class will be set to default values
   */
  constructor(param: FeedstockBmpPotentialAI1Interface = {
    lipidFat: new UnitValue(),
    protein: new UnitValue(),
    hemicellulose: new UnitValue(),
    cellulose: new UnitValue(),
    lignin: new UnitValue(),
    nonFibreCarbohydrate: new UnitValue(),
    ch4ContentEnabled: false,
    ch4Content: new UnitValue()
  }) {
    this.lipidFat = new UnitValue(param.lipidFat);
    this.protein = new UnitValue(param.protein);
    this.hemicellulose = new UnitValue(param.hemicellulose);
    this.cellulose = new UnitValue(param.cellulose);
    this.lignin = new UnitValue(param.lignin);
    this.nonFibreCarbohydrate = new UnitValue(param.nonFibreCarbohydrate);
    this.ch4ContentEnabled = param.ch4ContentEnabled ?? false;
    this.ch4Content = new UnitValue(param.ch4Content);
  }

  /**
   * returns a separate instance of the provided FeedstockBmpPotentialAI1 obj
   * @param feedstockBmpPotentialAI1 will be returned as a separate instance
   */
  static deepCopy(feedstockBmpPotentialAI1: FeedstockBmpPotentialAI1) {
    if (!feedstockBmpPotentialAI1) {
      throw new Error('unable to perform deep copy of FeedstockBmpPotentialAI1');
    }
    return new FeedstockBmpPotentialAI1(feedstockBmpPotentialAI1);
  }

  /**
   * Returns an array of InputSummaryItem containing inputs for each member of the FeedstockBmpPotentialAI1 class
   */
  getSummaryInputs(path: string[]) {
    let inputs: InputSummaryItem[] = [];
    inputs = InputSummaryUtils.performPush(inputs, FeedstockBmpPotentialAI1PropName.lipidFat, this.lipidFat, [...path, 'lipidFat']);
    inputs = InputSummaryUtils.performPush(inputs, FeedstockBmpPotentialAI1PropName.protein, this.protein, [...path, 'protein']);
    inputs = InputSummaryUtils.performPush(inputs, FeedstockBmpPotentialAI1PropName.hemicellulose, this.hemicellulose, [...path, 'hemicellulose']);
    inputs = InputSummaryUtils.performPush(inputs, FeedstockBmpPotentialAI1PropName.cellulose, this.cellulose, [...path, 'cellulose']);
    inputs = InputSummaryUtils.performPush(inputs, FeedstockBmpPotentialAI1PropName.lignin, this.lignin, [...path, 'lignin']);
    inputs = InputSummaryUtils.performPush(inputs, FeedstockBmpPotentialAI1PropName.nonFibreCarbohydrate, this.nonFibreCarbohydrate, [...path, 'nonFibreCarbohydrate']);
    inputs = InputSummaryUtils.performPush(inputs, FeedstockBmpPotentialAI1PropName.ch4ContentEnabled, this.ch4ContentEnabled, [...path, 'ch4ContentEnabled']);
    if (this.ch4ContentEnabled) {
      inputs = InputSummaryUtils.performPush(inputs, FeedstockBmpPotentialAI1PropName.ch4Content, this.ch4Content, [...path, 'ch4Content']);
    }
    return inputs;
  }
}


/**
 * An enum that contains a proper display name for properties of the FeedstockBmpPotentialAI1 class.
 *
 */
export enum FeedstockBmpPotentialAI1PropName {
  lipidFat = 'INPUT_SUMMARY.FEEDSTOCK.BMP_POTENTIAL.AI1.LIPID_FAT',
  protein = 'INPUT_SUMMARY.FEEDSTOCK.BMP_POTENTIAL.AI1.PROTEIN',
  hemicellulose = 'INPUT_SUMMARY.FEEDSTOCK.BMP_POTENTIAL.AI1.HEMICELLULOSE',
  cellulose = 'INPUT_SUMMARY.FEEDSTOCK.BMP_POTENTIAL.AI1.CELLULOSE',
  lignin = 'INPUT_SUMMARY.FEEDSTOCK.BMP_POTENTIAL.AI1.LIGNIN',
  nonFibreCarbohydrate = 'INPUT_SUMMARY.FEEDSTOCK.BMP_POTENTIAL.AI1.NONFIBCARB',
  ch4ContentEnabled = 'INPUT_SUMMARY.FEEDSTOCK.BMP_POTENTIAL.AI1.CH4CONTENT_ENABLED',
  ch4Content = 'INPUT_SUMMARY.FEEDSTOCK.BMP_POTENTIAL.AI1.CH4CONTENT',
  id_lipidFat = 'lipidFat',
  id_protein = 'protein',
  id_hemicellulose = 'hemicellulose',
  id_cellulose = 'cellulose',
  id_lignin = 'lignin',
  id_nonFibreCarbohydrate = 'non-fibre carbohydrate',
  id_ch4ContentEnabled = 'CH4 content of biogas enabled',
  id_ch4Content = 'CH4 content of biogas'
}
